import {
  type AxiosError,
  type AxiosProgressEvent,
  type AxiosRequestConfig,
  type AxiosResponse,
  isCancel,
} from 'axios';
import { use, useEffect, useReducer, useRef } from 'react';
import joinUrl from 'url-join';

import { addMessage } from '../../messages/state/messagesActions';
import {
  MessageFormat,
  MessageLevel,
} from '../../messages/types/messagesTypes';
import { apiPrefix } from '../config/apiUrls/apiPrefix';
import { BotDetectionContext } from '../contexts/BotDetection';
import { Namespace } from '../types/namespaces';
import fetchData from '../utils/fetchData';
import { sendGtagEventApiData } from '../utils/gtagEvents';
import * as logging from '../utils/logging';
import withRetry, { isSafeMethod } from '../utils/withRetry';
import { type UserPreferenceKey } from './useLocalStorage';
import useMessagesDispatch from './useMessagesDispatch';

export type CustomError = AxiosError<undefined | { messages?: string[] }>;

const invalidFieldMessage = /Invalid fields parameter value '(?<field>[^']*)'/;
const namespacedURL = new RegExp(
  joinUrl(
    apiPrefix,
    `(?<namespace>${Object.values(Namespace).join('|')})`,
    'search'
  )
);

// TODO: possible improvement:
// See https://developers.google.com/web/tools/workbox/modules/workbox-broadcast-update#using_broadcast_update
// Listen for a service worker message saying that there is new data to get
// fresher data from cache and replace the data within this custom hook

// Threshold under which a progress event will not be emitted
// This is in order to avoid unecessary renders
const PROGRESS_THRESHOLD = 2_000; // 2s

export type UseDataAPIState<T> = {
  loading: boolean;
  progress?: number;
  data?: T;
  status?: AxiosResponse['status'];
  statusText?: AxiosResponse['statusText'];
  headers?: AxiosResponse['headers'];
  error?: CustomError;
  redirectedTo?: string;
  url?: string | null;
};

enum ActionType {
  RESET = 'RESET',
  INIT = 'INIT',
  PROGRESS = 'PROGRESS',
  SUCCESS = 'SUCCESS',
  ERROR = 'ERROR',
}

type Action<T> =
  | { type: ActionType.RESET }
  | { type: ActionType.INIT; url?: string }
  | { type: ActionType.PROGRESS; progress: number }
  | {
      type: ActionType.SUCCESS;
      response?: AxiosResponse<T>;
      originalURL?: string;
      progress?: 1;
    }
  | { type: ActionType.ERROR; error: CustomError };

const createReducer =
  <T>() =>
  (state: UseDataAPIState<T>, action: Action<T>): UseDataAPIState<T> => {
    // eslint-disable-next-line default-case
    switch (action.type) {
      case ActionType.RESET:
        return { loading: false };
      case ActionType.INIT:
        return {
          loading: true,
          url: action.url,
        };
      case ActionType.PROGRESS:
        return {
          ...state,
          progress: action.progress,
        };
      case ActionType.SUCCESS:
        // eslint-disable-next-line no-case-declarations
        const newState: UseDataAPIState<T> = {
          ...state,
          loading: false,
          progress: action.progress,
          data: action.response?.data,
          status: action.response?.status,
          statusText: action.response?.statusText,
          headers: action.response?.headers,
        };
        if (
          action.response?.request?.responseURL &&
          action.response.request.responseURL !== action.originalURL
        ) {
          newState.redirectedTo = action.response.request.responseURL;
        } else if (
          // Issue with casing in axios-mock-adapter?
          action.response?.request?.responseUrl &&
          action.response.request.responseUrl !== action.originalURL
        ) {
          newState.redirectedTo = action.response.request.responseUrl;
        }
        return newState;
      case ActionType.ERROR:
        return {
          ...state,
          loading: false,
          progress: undefined,
          status: action.error.response?.status,
          statusText: action.error.response?.statusText,
          headers: action.error.response?.headers,
          error: action.error,
        };
    }
  };

const botProtectedPatterns = [/www\.ebi\.ac\.uk\/proteins\/api/];

function useDataApi<T>(
  url?: string | null,
  options?: AxiosRequestConfig
): UseDataAPIState<T> {
  const [state, dispatch] = useReducer(createReducer<T>(), { loading: !!url });
  const messagesDispatch = useMessagesDispatch();
  const optionsRef = useRef(options);

  useEffect(() => {
    if (options !== optionsRef.current) {
      optionsRef.current = options;
    }
  }, [options]);

  // Logic to block loading some URLs until we know a human loaded the page
  // Cannot be within the useEffect below, otherwise causes a reload and flash
  // for resources that were not blocked by the robot detection logic
  let urlToLoad = url;
  const botDetectionStatus = use(BotDetectionContext);
  if (url && botDetectionStatus === 'undetermined') {
    for (const pattern of botProtectedPatterns) {
      if (pattern.test(url)) {
        urlToLoad = null;
      }
    }
  }

  useEffect(() => {
    // need this variable to ensure state updates don't occur when cancelled/unmounted
    // https://github.com/facebook/react/issues/14369#issuecomment-468267798
    let didCancel = false;

    // we don't require a URL, we just don't need data anymore
    // start afresh, so that asking for the same URL again doesn't first get
    // back what the previous request ended with (e.g. its error)
    if (!urlToLoad) {
      dispatch({ type: ActionType.RESET });
      return;
    }

    dispatch({ type: ActionType.INIT, url: urlToLoad });

    // variables to handle cancellation
    const controller = new AbortController();
    // Ours is the signal the request gets, so that unmounting always cancels;
    // a caller's own signal is honoured by folding it into ours rather than
    // by letting it replace ours. Scoped to our signal so the listener goes
    // away with the request instead of outliving it on the caller's signal.
    const callerSignal = optionsRef.current?.signal;
    if (callerSignal) {
      if (callerSignal.aborted) {
        controller.abort();
      } else {
        // Optional call: axios types the signal loosely, as anything AbortSignal-like
        callerSignal.addEventListener?.('abort', () => controller.abort(), {
          once: true,
          signal: controller.signal,
        });
      }
    }

    // Retrying replays the request, so it is only safe for a method that
    // reads. Callers can override the method and at least one (the contact
    // form) POSTs through this hook -- replaying that would send the message
    // more than once.
    const retryEnabled = isSafeMethod(optionsRef.current?.method);

    // actual request
    const attempt = () => {
      // to keep track of the last time we dispatched a progress action.
      // Scoped to the attempt so a retried download doesn't report progress
      // that appears to go backwards.
      let lastProgressDate: number;
      // fetchData's second parameter is axios's deprecated CancelToken; the
      // AbortSignal goes through the request config instead. Ours last, on
      // purpose: it already carries the caller's (see above).
      return fetchData<T>(urlToLoad, undefined, {
        ...optionsRef.current,
        signal: controller.signal,
        onDownloadProgress: (progressEvent: AxiosProgressEvent) => {
          optionsRef.current?.onDownloadProgress?.(progressEvent);
          if (didCancel || progressEvent.progress === undefined) {
            return;
          }
          const now = Date.now();
          // If last time we dispatched a progress action was less than threshold
          if (lastProgressDate && now - lastProgressDate < PROGRESS_THRESHOLD) {
            // skip, don't want to refresh too often
            return;
          }
          dispatch({
            type: ActionType.PROGRESS,
            progress: progressEvent.progress,
          });
          lastProgressDate = now;
        },
      });
    };

    withRetry(attempt, {
      enabled: retryEnabled,
      signal: controller.signal,
      // fetchData reports a 'fail' for every attempt; pair the ones we
      // replay with a 'retry' so the two can be told apart downstream
      onRetry: () => sendGtagEventApiData('retry', urlToLoad),
    }).then(
      // handle ok
      (response: AxiosResponse<T>) => {
        if (didCancel) {
          return;
        }
        dispatch({
          type: ActionType.SUCCESS,
          response,
          originalURL: urlToLoad,
          progress: 1,
        });
      },
      // catch error
      (error: CustomError) => {
        // Ignore 404 because it might mean "valid request but no data"
        if (error.response?.status && error.response.status !== 404) {
          logging.error(error, {
            tags: { origin: 'useDataApi', url: urlToLoad },
          });
        }
        if (isCancel(error) || didCancel) {
          return;
        }
        dispatch({ type: ActionType.ERROR, error });
      }
    );

    // handle unmounting of the hook

    return () => {
      controller.abort();
      didCancel = true;
    };
  }, [urlToLoad, optionsRef]);

  useEffect(() => {
    // TODO: when refactoring, accept a onError callback to do this from outside
    if (state.status === 400) {
      // Just in case a field is updated in the server and is no longer valid
      try {
        for (const message of state.error?.response?.data?.messages || []) {
          const messageMatch = message.match(invalidFieldMessage);
          const invalidField = messageMatch?.groups?.field;
          if (!messageMatch) {
            continue;
          }
          const nsMatch = state.url?.match(namespacedURL);
          if (!nsMatch?.groups?.namespace) {
            continue;
          }
          const key =
            `table columns for ${nsMatch?.groups?.namespace}` as UserPreferenceKey;
          const faultyArray: string[] = JSON.parse(
            localStorage.getItem(key) as string
          );
          const noFaults = faultyArray.filter(
            (column) =>
              // Clean up any wrong type of value, and the faulty value too
              column && typeof column === 'string' && column !== invalidField
          );
          // If no valid columns then store undefined which will cause the
          // default columns to be used.
          const correctArray = JSON.stringify(
            noFaults.length ? noFaults : undefined
          );
          localStorage.setItem(key, correctArray);
          // Signals to all useLocalStorage hooks in use to rerender
          window.dispatchEvent(
            new StorageEvent('storage', {
              key,
              newValue: correctArray,
              storageArea: window.localStorage,
            })
          );
          return;
        }
      } catch {
        /**/
      }
      const messageContent =
        state.error?.response?.data?.messages?.join(',') || '400 Error';
      messagesDispatch(
        addMessage({
          id: messageContent,
          content: messageContent,
          format: MessageFormat.POP_UP,
          level: MessageLevel.FAILURE,
          displayTime: 5_000,
        })
      );
    }
  }, [messagesDispatch, state.status, state.error, state.url]);

  // when changing the URL, the state is set asynchronously, this is to set it
  // to loading synchronously to avoid using previous data
  if (state.url !== url) {
    return { loading: Boolean(url), url };
  }

  return state;
}

export default useDataApi;
